import React, { useState, useEffect } from 'react'
import styles from './ServersPage.module.scss'
import ServersService from '../../../service/ServersService'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import SearchBar from '../MoviePage/components/SearchBar/SearchBar'
import { GameControllerIcon, PeopleIcon, StarIcon, HeartIcon, SearchIcon, GlobeIcon, CommentIcon } from '../../../components/CustomIcons'

const ServersPage = ({ embedded = false, goBack }) => {
  const [servers, setServers] = useState([])
  const [serverTypes, setServerTypes] = useState([])
  const [serverRegions, setServerRegions] = useState([])
  const [featuredServers, setFeaturedServers] = useState([])
  const [serverStats, setServerStats] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [view, setView] = useState('all') // all, featured, favorites
  const [selectedServer, setSelectedServer] = useState(null)
  const [showServerModal, setShowServerModal] = useState(false)
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    region: '',
    status: ''
  })
  const [sortBy, setSortBy] = useState('-current_players')
  const [likedServers, setLikedServers] = useState(new Set())
  const [animatingLikes, setAnimatingLikes] = useState(new Set())
  const [favoritedServers, setFavoritedServers] = useState(new Set())
  const [animatingFavorites, setAnimatingFavorites] = useState(new Set())
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'))
  const [copiedServerId, setCopiedServerId] = useState(null)

  useEffect(() => {
    // Проверяем авторизацию при монтировании
    const checkAuth = () => {
      setIsAuthenticated(!!localStorage.getItem('token'));
    };
    checkAuth();
    
    // Слушаем события изменения localStorage (при входе/выходе)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  useEffect(() => {
    // Первоначальная загрузка данных только при монтировании
    const initializeServers = async () => {
      try {
        await loadData()
      } catch (error) {
        console.error('Ошибка загрузки серверов:', error)
      }
    }
    initializeServers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Отдельный effect для обновления при смене view
  useEffect(() => {
    if (view !== 'all') { // Не перезагружаем для первой загрузки
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  // Функция применения фильтров с защитой от множественных вызовов
  const applyFilters = () => {
    if (loading) return // Предотвращаем множественные вызовы
    setCurrentPage(1)
    loadData()
  }

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (view === 'all') {
        await loadServers()
      } else if (view === 'featured') {
        await loadFeaturedServers()
      } else if (view === 'favorites') {
        await loadFavoriteServers()
      }
      
      await loadServerTypes()
      await loadServerRegions()
      await loadServerStats()
    } catch (err) {
      setError('Ошибка загрузки игровых серверов')
      setRetryAction(() => loadData)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const loadServers = async () => {
    const response = await ServersService.getServers({
      page: currentPage,
      search: filters.search,
      type: filters.type,
      region: filters.region,
      status: filters.status,
      ordering: sortBy
    })
    setServers(response.data.results || response.data || [])
    setTotalPages(response.data.total_pages || 1)
  }

  const loadFeaturedServers = async () => {
    const response = await ServersService.getFeaturedServers()
    setFeaturedServers(response.data || [])
  }

  const loadFavoriteServers = async () => {
    const response = await ServersService.getFavoriteServers()
    setServers(response.data.map(fav => fav.server) || [])
  }

  const loadServerTypes = async () => {
    try {
      const response = await ServersService.getServerTypes()
      setServerTypes(response.data || [])
    } catch (err) {
      console.error('Ошибка загрузки типов серверов:', err)
    }
  }

  const loadServerRegions = async () => {
    try {
      const response = await ServersService.getServerRegions()
      setServerRegions(response.data || [])
    } catch (err) {
      console.error('Ошибка загрузки регионов:', err)
    }
  }

  const loadServerStats = async () => {
    try {
      const response = await ServersService.getServerStats()
      setServerStats(response.data || {})
    } catch (err) {
      console.error('Ошибка загрузки статистики:', err)
    }
  }

  const handleServerClick = (server) => {
    setSelectedServer(server)
    setShowServerModal(true)
  }

  const handleCopyAddress = async (server, e) => {
    e.stopPropagation()
    const address = `${server.ip_address}:${server.port}`
    
    try {
      await navigator.clipboard.writeText(address)
      setCopiedServerId(server.id)
      
      // Убираем уведомление через 2 секунды
      setTimeout(() => {
        setCopiedServerId(null)
      }, 2000)
    } catch (err) {
      console.error('Ошибка копирования:', err)
    }
  }

  const handleToggleFavorite = async (server) => {
    if (!isAuthenticated) return
    
    setAnimatingFavorites(prev => new Set(prev).add(server.id))
    
    setTimeout(() => {
      setAnimatingFavorites(prev => {
        const newSet = new Set(prev)
        newSet.delete(server.id)
        return newSet
      })
    }, 600)

    try {
      await ServersService.toggleServerFavorite(server.slug)
      // Обновляем локально без полной перезагрузки
      setFavoritedServers(prev => {
        const newSet = new Set(prev)
        if (newSet.has(server.id)) {
          newSet.delete(server.id)
        } else {
          newSet.add(server.id)
        }
        return newSet
      })
      setServers(prev => prev.map(s => 
        s.slug === server.slug 
          ? { ...s, is_favorite: !s.is_favorite }
          : s
      ))
    } catch (err) {
      console.error('Ошибка добавления в избранное:', err)
    }
  }

  const toggleLike = (serverId) => {
    if (!isAuthenticated) return
    
    setAnimatingLikes(prev => new Set(prev).add(serverId))
    
    setTimeout(() => {
      setAnimatingLikes(prev => {
        const newSet = new Set(prev)
        newSet.delete(serverId)
        return newSet
      })
    }, 600)

    setLikedServers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(serverId)) {
        newSet.delete(serverId)
      } else {
        newSet.add(serverId)
      }
      return newSet
    })
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    // Обновляем только по кнопке "Применить фильтры"
  }

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }))
    // Обновляем только по кнопке поиска или Enter
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#4CAF50'
      case 'offline': return '#f44336'
      case 'maintenance': return '#FF9800'
      case 'loading': return '#2196F3'
      default: return '#9E9E9E'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Онлайн'
      case 'offline': return 'Оффлайн'
      case 'maintenance': return 'Техобслуживание'
      case 'loading': return 'Загружается'
      default: return 'Неизвестно'
    }
  }

  if (loading && servers.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка серверов...</p>
      </div>
    )
  }

  const currentServers = view === 'featured' ? featuredServers : servers

  return (
    <div className={styles.serversPage}>
      {!embedded && (
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            <span className={styles.icon}><GameControllerIcon size={32} /></span>
            Игровые серверы
          </h1>
          <p className={styles.pageSubtitle}>
            Найдите лучшие игровые серверы для ваших любимых игр
          </p>
        </div>
      )}

      {/* Статистика */}
      {serverStats && Object.keys(serverStats).length > 0 && (
        <div className={styles.statsCards}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <GameControllerIcon size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{serverStats.total_servers || 0}</div>
              <div className={styles.statLabel}>Всего серверов</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardOnline}`}>
            <div className={styles.statIcon}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{serverStats.online_servers || 0}</div>
              <div className={styles.statLabel}>Онлайн</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardOffline}`}>
            <div className={styles.statIcon}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{serverStats.offline_servers || 0}</div>
              <div className={styles.statLabel}>Оффлайн</div>
            </div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardPlayers}`}>
            <div className={styles.statIcon}>
              <PeopleIcon size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{serverStats.total_players || 0}</div>
              <div className={styles.statLabel}>Игроков</div>
            </div>
          </div>
        </div>
      )}

      {/* Навигация */}
      {false && (
        <div className={styles.navigation}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${view === 'all' ? styles.active : ''}`}
            onClick={() => {
              setView('all')
            }}
          >
            Все серверы
          </button>
          <button 
            className={`${styles.tab} ${view === 'featured' ? styles.active : ''}`}
            onClick={() => {
              setView('featured')
            }}
          >
            Рекомендуемые
          </button>
          {isAuthenticated && (
            <button 
              className={`${styles.tab} ${view === 'favorites' ? styles.active : ''}`}
              onClick={() => {
                setView('favorites')
              }}
            >
              Избранные
            </button>
          )}
        </div>
      </div>
      )}

      {/* Фильтры и поиск */}
      {false && (
        <div className={styles.serverFilters}>
        <div className={styles.filterGroup}>
          <label>Поиск</label>
          <div className={styles.searchContainer}>
            <SearchBar 
              onSearch={handleSearch}
              placeholder="Поиск серверов..."
            />
            <button 
              onClick={applyFilters}
              className={styles.searchButton}
            >
              <SearchIcon size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Найти
            </button>
          </div>
        </div>
        
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label>Тип игры</label>
            <select 
              value={filters.type} 
              onChange={(e) => handleFilterChange({ ...filters, type: e.target.value })}
              className={styles.filterSelect}
            >
              <option value="">Все типы игр</option>
              {serverTypes.map(type => (
                <option key={type.id} value={type.name}>{type.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label>Регион</label>
            <select 
              value={filters.region} 
              onChange={(e) => handleFilterChange({ ...filters, region: e.target.value })}
              className={styles.filterSelect}
            >
              <option value="">Все регионы</option>
              {serverRegions.map(region => (
                <option key={region.id} value={region.code}>{region.name}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label>Статус</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange({ ...filters, status: e.target.value })}
              className={styles.filterSelect}
            >
              <option value="">Все статусы</option>
              <option value="online">Онлайн</option>
              <option value="offline">Оффлайн</option>
              <option value="maintenance">Техобслуживание</option>
            </select>
          </div>
          
          <div className={styles.filterGroup}>
            <label>Сортировка</label>
            <select 
              value={sortBy} 
              onChange={(e) => {
                setSortBy(e.target.value)
              }}
              className={styles.filterSelect}
            >
              <option value="name">По имени</option>
              <option value="-current_players">По игрокам</option>
              <option value="-last_ping">По пингу</option>
              <option value="region">По региону</option>
              <option value="status">По статусу</option>
            </select>
          </div>
        </div>
      </div>
      )}



      {/* Контент */}
      <div className={styles.content}>
        <div className={styles.serversGrid}>
          {currentServers.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}><GameControllerIcon size={64} /></div>
              <h3>Серверы не найдены</h3>
              <p>Попробуйте изменить фильтры поиска</p>
            </div>
          ) : (
            currentServers.map(server => (
              <div key={server.id} className={styles.serverCard}>
                <div className={styles.serverHeader}>
                  <div className={styles.serverStatus}>
                    <span 
                      className={styles.statusDot}
                      style={{ backgroundColor: getStatusColor(server.status) }}
                    />
                    <span className={styles.statusText}>
                      {getStatusText(server.status)}
                    </span>
                  </div>
                  {server.is_featured && (
                    <span className={styles.featuredBadge}><StarIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Рекомендуемый</span>
                  )}
                </div>
                
                <div className={styles.serverInfo}>
                  <h3 className={styles.serverName}>{server.name}</h3>
                  <div className={styles.serverDetails}>
                    <span className={styles.gameType}>{server.server_type_name}</span>
                    <span className={styles.region}>
                      {server.region_flag} {server.region_name}
                    </span>
                  </div>
                  
                  <div className={styles.serverStats}>
                    <div className={styles.stat}>
                      <span className={styles.statLabel}>Игроков:</span>
                      <span className={styles.statValue}>
                        {server.current_players}/{server.max_players}
                      </span>
                    </div>
                    <div className={styles.stat}>
                      <span className={styles.statLabel}>Рейтинг:</span>
                      <span className={styles.statValue}>
                        <StarIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> {typeof server.rating_avg === 'number' ? server.rating_avg.toFixed(1) : server.rating_avg || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className={styles.serverAddressWrapper}>
                    <div 
                      className={styles.serverAddress}
                      onClick={(e) => handleCopyAddress(server, e)}
                      title="Нажмите, чтобы скопировать"
                    >
                      <span className={styles.addressLabel}>IP адрес:</span>
                      <code className={styles.addressCode}>{server.ip_address}:{server.port}</code>
                      <svg className={styles.copyIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {copiedServerId === server.id && (
                      <div className={styles.copiedNotification}>
                        ✓ Скопировано!
                      </div>
                    )}
                  </div>

                  {server.description && (
                    <p className={styles.serverDescription}>{server.description}</p>
                  )}
                </div>

                <div className={styles.serverActions}>
                  <button 
                    className={styles.viewButton}
                    onClick={() => handleServerClick(server)}
                  >
                    Подробнее
                  </button>
                  {isAuthenticated && (
                    <button 
                      className={`${styles.likeButton} ${likedServers.has(server.id) ? styles.liked : ''} ${animatingLikes.has(server.id) ? styles.animating : ''}`}
                      onClick={() => toggleLike(server.id)}
                      title="Нравится"
                    >
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                  {isAuthenticated && (
                    <button 
                      className={`${styles.likeButton} ${favoritedServers.has(server.id) || server.is_favorited ? styles.liked : ''} ${animatingFavorites.has(server.id) ? styles.animating : ''}`}
                      onClick={() => handleToggleFavorite(server)}
                      title="Добавить в избранное"
                    >
                      <HeartIcon size={20} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {loading && (
          <div className={styles.loadingMore}>
            <Loader size="medium" />
          </div>
        )}

        {totalPages > 1 && view === 'all' && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Модальные окна */}
      {showServerModal && selectedServer && (
        <div className={styles.modal} onClick={() => setShowServerModal(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{selectedServer.name}</h2>
              <button 
                className={styles.closeButton}
                onClick={() => setShowServerModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.serverModalInfo}>
                <div className={styles.modalAddressWrapper}>
                  <div 
                    className={styles.modalServerAddress}
                    onClick={(e) => handleCopyAddress(selectedServer, e)}
                    title="Нажмите, чтобы скопировать"
                  >
                    <span className={styles.addressLabel}>IP адрес:</span>
                    <code className={styles.addressCode}>{selectedServer.ip_address}:{selectedServer.port}</code>
                    <svg className={styles.copyIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {copiedServerId === selectedServer.id && (
                    <div className={styles.copiedNotification}>
                      ✓ Скопировано!
                    </div>
                  )}
                </div>
                <div className={styles.infoRow}>
                  <strong>Игра:</strong> {selectedServer.server_type_name}
                </div>
                <div className={styles.infoRow}>
                  <strong>Регион:</strong> {selectedServer.region_name}
                </div>
                <div className={styles.infoRow}>
                  <strong>Игроков:</strong> {selectedServer.current_players}/{selectedServer.max_players}
                </div>
                <div className={styles.infoRow}>
                  <strong>Версия:</strong> {selectedServer.version}
                </div>
                {selectedServer.map_name && (
                  <div className={styles.infoRow}>
                    <strong>Карта:</strong> {selectedServer.map_name}
                  </div>
                )}
                <div className={styles.infoRow}>
                  <strong>Статус:</strong> 
                  <span style={{ color: getStatusColor(selectedServer.status) }}>
                    {getStatusText(selectedServer.status)}
                  </span>
                </div>
                {selectedServer.description && (
                  <div className={styles.description}>
                    <strong>Описание:</strong>
                    <p>{selectedServer.description}</p>
                  </div>
                )}
                {selectedServer.website && (
                  <div className={styles.links}>
                    <a href={selectedServer.website} target="_blank" rel="noopener noreferrer">
                      <GlobeIcon size={18} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Веб-сайт
                    </a>
                  </div>
                )}
                {selectedServer.discord && (
                  <div className={styles.links}>
                    <a href={selectedServer.discord} target="_blank" rel="noopener noreferrer">
                      <CommentIcon size={18} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Discord
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ServersPage